import React, { useEffect, useState } from 'react';
import { Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { adminApi } from '../../api';

import ConfirmationModal from '../common/ConfirmationModal';
import Loader from '../common/Loader';

export default function EndUserPassportMigrationModal({
  show,
  setShow,
  handle,
  statusOnly,
  toggleStatusOnly,
  getEndUser,
  userRegisteredAtPriority,
}) {
  const [includeWalletsWithBalance, setIncludeWalletsWithBalance] = useState(false);
  const [migrationRequestSent, setMigrationRequestSent] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedPiUUIDS, setSelectedPiUUIDS] = useState(new Set());
  const [pis, setPis] = useState({});
  const [loading, setLoading] = useState(true);
  const [entityMigrationErrors, setEntityMigrationErrors] = useState(false);
  const [walletMigrationResults, setWalletMigrationResults] = useState({});
  const [bankAccountMigrationResults, setBankAccountMigrationResults] = useState({});
  const [registered, setRegistered] = useState(userRegisteredAtPriority);
  const [message, setMessage] = useState(false);
  const { t } = useTranslation();

  const migrateUserToPriority = async () => {
    const response = await adminApi.migrateToPriority(
      handle,
      includeWalletsWithBalance,
      Array.from(selectedPiUUIDS)
    );
    setSuccess(response.success);
    setMigrationRequestSent(true);
    setMessage(response.message);
    setRegistered(response.success);
    if (!response.success) {
      setEntityMigrationErrors(response.failed_entity_migration_errors);
    } else {
      setWalletMigrationResults(response.wallets);
      setBankAccountMigrationResults(response.bank_accounts);
    }
  };

  const getPriorityMigrationStatus = async () => {
    const response = await adminApi.getPriorityMigrationStatus(handle);
    if (response.success) {
      setPis(response.payment_instruments);
    }
    setLoading(false);
  };

  const resultsLog = (errors) => (
    <div className="bg-red-50 border border-red-400 text-red-700 p-4 rounded-md mb-6">
      <pre className="overflow-x-auto text-sm">{JSON.stringify(errors, null, 2)}</pre>
    </div>
  );

  const pisMigrated =
    Object.values(bankAccountMigrationResults).length !== 0 ||
    Object.values(walletMigrationResults).length !== 0;

  const resultsModalBody = (
    <div>
      <div className={`mb-3 ${success ? '' : 'text-danger'}`}>{message}</div>
      {entityMigrationErrors && <div>{resultsLog(entityMigrationErrors)}</div>}
      {Object.values(walletMigrationResults).length !== 0 && (
        <>
          <div className="my-3">{t('admin.priorityMigration.walletMigrationResults')}</div>
          <div>{resultsLog(walletMigrationResults)}</div>
        </>
      )}
      {Object.values(bankAccountMigrationResults).length !== 0 && (
        <>
          <div className="m-3">{t('admin.priorityMigration.bankAccountMigrationResults')}</div>
          <div>{resultsLog(bankAccountMigrationResults)}</div>
        </>
      )}
      {pisMigrated && (
        <div className="mt-2 text-warning">
          {t('admin.priorityMigration.readThePIMigrationResults')}
        </div>
      )}
    </div>
  );

  const piSelectionForm = (
    <Form.Group controlId="includeWalletsCheckbox">
      {!Object.values(pis).every((data) => !data.has_balance) && (
        <>
          <h4 className="mb-3">{t('admin.priorityMigration.includeWalletsSubheader')}</h4>
          <Form.Check
            className="mb-3"
            type="checkbox"
            label={t('admin.priorityMigration.buttons.includeWalletsWithBalance')}
            checked={includeWalletsWithBalance}
            onChange={(e) => setIncludeWalletsWithBalance(e.target.checked)}
          />
          <div className="text-warning mb-3">
            {t('admin.priorityMigration.includeWalletsWithBalanceWarning')}
          </div>
        </>
      )}

      {Object.values(pis).length !== 0 ? (
        <>
          <h4 className="mb-3">{t('admin.priorityMigration.piSubheader')}</h4>
          <div className="mb-3 text-info">
            {t('admin.priorityMigration.migrationEligiblePiDescription')}
          </div>

          {Object.entries(pis).map(([piUuid, data]) => {
            const showError = data.has_balance && !includeWalletsWithBalance;

            return (
              <Form.Group key={piUuid} controlId={`${piUuid}-checkbox`} className="mb-3">
                <Form.Check
                  key={`${piUuid}-form`}
                  className="mb-3"
                  type="checkbox"
                  label={`${piUuid}: ${data.detail}`}
                  disabled={!data.can_migrate || showError}
                  checked={selectedPiUUIDS.has(piUuid)}
                  onChange={() => {
                    const newSet = new Set(selectedPiUUIDS);
                    if (newSet.has(piUuid)) {
                      newSet.delete(piUuid);
                    } else {
                      newSet.add(piUuid);
                    }
                    setSelectedPiUUIDS(newSet);
                  }}
                  isInvalid={showError}
                  feedback={t('admin.priorityMigration.piHasBalanceError')}
                  feedbackType="invalid"
                />
              </Form.Group>
            );
          })}
        </>
      ) : (
        <div>{t('admin.priorityMigration.noPisAreEligible')}</div>
      )}
    </Form.Group>
  );

  const piList = (
    <>
      <h4 className="mb-3">{t('admin.priorityMigration.piSubheader')}</h4>
      <div className="mb-3 text-info">
        {t('admin.priorityMigration.migrationEligiblePiDescription')}
      </div>
      {Object.values(pis).length ? (
        <Table responsive className="custom-detail">
          <thead>
            <tr className="border">
              <th className="border-end">{t('common:labels.paymentInstrument')}</th>
              <th>{t('common:labels.status')}</th>
            </tr>
          </thead>
          {Object.entries(pis).map(([piUuid, data]) => {
            return (
              <tr key={`${piUuid}-list`} className="border">
                <td className={`border-end ${data.can_migrate && ' text-danger'}`}>{piUuid}</td>
                <td className={`${data.can_migrate && ' text-danger'}`}>{data.detail}</td>
              </tr>
            );
          })}
        </Table>
      ) : (
        <div>{t('admin.priorityMigration.noPisFound')}</div>
      )}
    </>
  );

  const modalBody = () => {
    if (statusOnly) return piList;
    if (migrationRequestSent) return resultsModalBody;
    return piSelectionForm;
  };

  const submitButtonText = () => {
    if (statusOnly) return t('admin.priorityMigration.buttons.attemptMigration');
    if (migrationRequestSent) return t('common:buttons.back');
    return t('common:buttons.send');
  };

  const shouldDisableSubmit = () => {
    if (!registered) return false;
    const noMigrationEligiblePis = Object.values(pis).every((pi) => pi.can_migrate === false);
    if (statusOnly) return noMigrationEligiblePis;
    if (noMigrationEligiblePis) return true;
    if (selectedPiUUIDS.size === 0) return true;

    return false;
  };

  const handleSubmit = async () => {
    if (statusOnly) {
      toggleStatusOnly();
    } else if (!migrationRequestSent) {
      await migrateUserToPriority();
    } else {
      setSelectedPiUUIDS(new Set());
      setMigrationRequestSent(false);
      setEntityMigrationErrors(false);
      setWalletMigrationResults({});
      setBankAccountMigrationResults({});
      setIncludeWalletsWithBalance(false);
      await getPriorityMigrationStatus();
    }
  };

  const handleCancel = () => {
    getEndUser();
    setShow(false);
    setMigrationRequestSent(false);
  };

  useEffect(() => {
    if (handle) {
      getPriorityMigrationStatus(handle);
    }
  }, [handle]);

  if (loading) return <Loader />;

  return (
    <ConfirmationModal
      show={show}
      size="lg"
      title={
        statusOnly
          ? t('admin.priorityMigration.statusTitle')
          : t('admin.priorityMigration.modalTitle')
      }
      renderMessageComponent={modalBody}
      buttonLabel={submitButtonText()}
      handleSubmit={handleSubmit}
      setShow={handleCancel}
      buttonDisabled={shouldDisableSubmit()}
      cancelButtonLabel={t('common:buttons.close')}
    />
  );
}
